<template>
    <div class="gift-cards">
        <section class="section page-wrapper">
            <div class="section__content">
                <h1>
                    {{ $t('pages.gift_cards.heading') }}
                </h1>
                <p>{{ $t('pages.gift_cards.offer') }}</p>
                <div class="gift-cards__divider" />

                <div class="gift-cards__section">
                    <p>{{ $t('pages.gift_cards.offer_text') }}</p>
                </div>
                <div class="gift-cards__section">
                    <h3>{{ $t('pages.gift_cards.reasons') }}</h3>
                    <ol>
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.gift_cards.reasons_text1') }}</span>
                        </li>
                        <li>
                            <svg-icon name="check_circle" />
                            <span>{{ $t('pages.gift_cards.reasons_text2') }}</span>
                        </li>
                    </ol>
                </div>
                <div class="gift-cards__section">
                    <div class="gift-cards__value">
                        <p>
                            <strong>{{ $t('pages.gift_cards.values') }}</strong>
                        </p>
                        <p>{{ giftCardPrices.value50 }}</p>
                        <a :href="$t('pages.gift_cards.value_50_link')">
                            {{ $t('pages.gift_cards.value_link_text', { amount: giftCardPrices.value50 }) }}
                        </a>
                    </div>
                    <div class="gift-cards__value">
                        <p>
                            <strong>{{ $t('pages.gift_cards.values') }}</strong>
                        </p>
                        <p>{{ giftCardPrices.value100 }}</p>
                        <a :href="$t('pages.gift_cards.value_100_link')">
                            {{ $t('pages.gift_cards.value_link_text', { amount: giftCardPrices.value100 }) }}
                        </a>
                    </div>
                    <div class="gift-cards__value">
                        <p>
                            <strong>{{ $t('pages.gift_cards.values') }}</strong>
                        </p>
                        <p>{{ giftCardPrices.value250 }}</p>
                        <a :href="$t('pages.gift_cards.value_250_link')">
                            {{ $t('pages.gift_cards.value_link_text', { amount: giftCardPrices.value250 }) }}
                        </a>
                    </div>
                    <div class="gift-cards__value">
                        <p>
                            <strong>{{ $t('pages.gift_cards.values') }}</strong>
                        </p>
                        <p>{{ giftCardPrices.value500 }}</p>
                        <a :href="$t('pages.gift_cards.value_500_link')">
                            {{ $t('pages.gift_cards.value_link_text', { amount: giftCardPrices.value500 }) }}
                        </a>
                    </div>
                </div>
                <div class="gift-cards__section">
                    {{ $t('pages.gift_cards.info') }}
                </div>
            </div>
            <Contact />
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Contact from '~/components/sections/Contact';

const GIFT_CARD_SIZES = {
    value50: 50000,
    value100: 100000,
    value250: 250000,
    value500: 500000,
};

const giftCardAmounts = {
    NOK: GIFT_CARD_SIZES,
    SEK: GIFT_CARD_SIZES,
    DKK: GIFT_CARD_SIZES,
    EUR: {
        value50: GIFT_CARD_SIZES.value50 / 10,
        value100: GIFT_CARD_SIZES.value100 / 10,
        value250: GIFT_CARD_SIZES.value250 / 10,
        value500: GIFT_CARD_SIZES.value500 / 10,
    },
    CHF: {
        value50: GIFT_CARD_SIZES.value50 / 10,
        value100: GIFT_CARD_SIZES.value100 / 10,
        value250: GIFT_CARD_SIZES.value250 / 10,
        value500: GIFT_CARD_SIZES.value500 / 10,
    },
};

export default {
    name: 'GiftCards',
    components: { Contact },
    nuxtI18n: {
        paths: {
            se: '/presentkort',
            'fi-sv': '/presentkort',
            en: '/gift-cards',
            de: '/gutscheine',
            at: '/gutscheine',
            fi: '/lahjakortti',
            no: '/gavekort',
            dk: '/gavekort',
            ch: '/gutscheine',
            fr: '/cartes-cadeaux',
        },
    },
    head() {
        return {
            title: this.$t('pages.gift_cards.heading'),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.$t('pages.gift_cards.seo_description'),
                },
            ],
        };
    },
    computed: {
        ...mapGetters(['siteCountryCode', 'defaultCurrency']),
        giftCardPrices() {
            const { price, formatAsPrice } = this.$options.filters;
            const currency = this.defaultCurrency.toUpperCase();

            return {
                value50: formatAsPrice(price(giftCardAmounts[currency].value50), currency, 0, false),
                value100: formatAsPrice(price(giftCardAmounts[currency].value100), currency, 0, false),
                value250: formatAsPrice(price(giftCardAmounts[currency].value250), currency, 0, false),
                value500: formatAsPrice(price(giftCardAmounts[currency].value500), currency, 0, false),
            };
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';
.gift-cards {
    h1 {
        @include font-size(24px);
        font-weight: bold;
        margin-bottom: 24px;
    }

    h3 {
        @include font-size(20px);
        font-weight: bold;
        margin-bottom: 16px;
    }

    &__section {
        margin-bottom: 32px;
    }

    .section__content {
        max-width: 680px;
    }

    .svg-icon {
        width: 24px;
        height: 24px;
        min-width: 24px;
        margin-right: 4px;
        color: $blue;
    }

    li {
        margin-bottom: 8px;
        display: flex;
    }

    &__divider {
        border-top: 1px solid $border-color;
        margin: 32px 0;
    }

    &__value {
        margin-bottom: 24px;

        p {
            margin-bottom: 8px;
        }
    }
}
</style>
